import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

import api, { studentsAPI } from "../../services/api";

import DataTable from '../../components/common/DataTable';
import SearchBar from '../../components/common/SearchBar';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';

import StudentForm from './StudentForm';

// import { getInitials } from '../../utils/helpers';
import { formatDate, getInitials } from '../../utils/helpers';

const API_BASE_URL = 'http://127.0.0.1:8000/api/students/students';

const StudentList = () => {

  const [students, setStudents] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const [filters, setFilters] = useState({
    status: '',
    gender: '',
    current_class: ''
  });

  const [classes, setClasses] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editStudent, setEditStudent] = useState(null);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewStudent, setViewStudent] = useState(null);

  // FETCH STUDENTS
  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: 15,
        search,
        ...filters
      };
      Object.keys(params).forEach(
        k => !params[k] && delete params[k]
      );
	const res = await studentsAPI.list(params);

	const studentData = res.data.results || res.data;

	const studentsWithSerial = studentData.map((student, index) => ({
	  ...student,
	  serial_number: ((page - 1) * 15) + index + 1,
	}));

	setStudents(studentsWithSerial);
	setCount(res.data.count || 0);
      console.log(res, "res===")
    } catch (err) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  // EXPORT STUDENTS
  const exportStudents = () => {
    window.open(
      `${API_BASE_URL}/export_students/`,
      '_blank'
    );
  };

  // IMPORT STUDENTS
  const importStudents = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(
        `${API_BASE_URL}/import_students/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      toast.success('Students imported successfully');

      fetchStudents();

    } catch (err) {
      console.log(err);
      toast.error('Import failed');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchStudents();
  }, [page, search, filters]);

  useEffect(() => {
    studentsAPI
      .classes()
      .then(r => setClasses(r.data.results || r.data))
      .catch(() => { });
  }, []);

  // DELETE
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this student?')) return;
    try {
      await studentsAPI.delete(id);
      toast.success('Student deleted');
      fetchStudents();
    } catch {
      toast.error('Failed to delete');
    }
  };

  // EDIT
  const openEdit = async (student) => {
    try {
      const response = await studentsAPI.get(student.id);
      setEditStudent(response.data);
      setShowModal(true);
    } catch (error) {
      console.log(error);
      toast.error('Failed to load student details');
    }
  };

  // ADD
  const openAdd = () => {
    setEditStudent(null);
    setShowModal(true);
  };

  // VIEW DETAILS
  const viewStudentDetails = async (id) => {
     try {
      const response = await studentsAPI.get(id);
      setViewStudent(response.data);
      setShowViewModal(true);
    } catch (error) {
      console.log(error);
      toast.error('Failed to load student details');
    }
    // console.log(id, row, "id=== row")
    // const student = students.find((s) => s.id === id);
    // if (student) {
    //   setViewStudent(student);
    //   setShowViewModal(true);
    // }
  };

  // TABLE COLUMNS
	const columns = [
		{
		key: 'serial_number',
		label: 'S.No',
		render: (v) => (
		  <span>{v}</span>
		)
	  },
    {
      key: 'photo',
      label: 'photo',
      render: (photo, row) => (

        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'linear-gradient(135deg,#1a3a5c,#0ea5e9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700
          }}
        >

          {
            photo
              ? (
                <img
                  src={photo}
                  alt=""
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                />
              )
              : getInitials(
                row.full_name ||
                `${row.first_name} ${row.last_name}`
              )
          }

        </div>
      )
    },

    {
      key: 'admission_number',
      label: 'Adm. No.',
      render: (v) => (
        <code style={{ fontSize: 12 }}>{v}</code>
      )
    },

    {
      key: 'full_name',
      label: 'Student Name',
      render: (v, row) => (
        <strong>
          {v || `${row.first_name} ${row.last_name}`}
        </strong>
      )
    },

    {
      key: 'gender',
      label: 'Gender',
      render: (v) =>
        v === 'M'
          ? '👦 Male'
          : v === 'F'
            ? '👧 Female'
            : v
    },

    {
      key: 'class_name',
      label: 'Class',
      render: (v, row) =>
        v
          ? `${v}${row.section_name ? ' - ' + row.section_name : ''}`
          : '—'
    },

    {
      key: 'phone',
      label: 'Phone',
      render: (v) => v || '—'
    },

    {
      key: 'status',
      label: 'Status',
      render: (v) => (
        <StatusBadge status={v} />
      )
    },

    {
      key: 'id',
      label: 'Actions',
      render: (id, row) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(row);
            }}
          >
            <i className="bi bi-pencil-square icon-sm"></i>
          </button>

          <button
            className="btn btn-sm btn-outline-info"
            onClick={(e) => {
              e.stopPropagation();
              viewStudentDetails(id);
            }}
          >
            <i className="bi bi-eye icon-sm"></i>
          </button>

          <button
            className="btn btn-sm btn-outline-danger"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(id);
            }}
          >
            <i className="bi bi-trash icon-sm"></i>
          </button>

        </div>
      )
    },
  ];

  return (

    <div>

      {/* PAGE HEADER */}
      <div className="page-header">

        <div>
          <h2>Students</h2>
          {/* <p>Manage student records and admissions</p> */}
        </div>

        <button
          className="btn btn-primary page-btn"
          onClick={openAdd}
        >
          <i className="lni lni-plus fw-bold fs-4 lh-0"></i>
          Add Student
        </button>

      </div>

      {/* FILTERS */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <SearchBar
                onSearch={setSearch}
                placeholder="Search by name, admission no., phone..."
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    status: e.target.value
                  })
                }
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="transferred">Transferred</option>
                <option value="graduated">Graduated</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={filters.gender}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    gender: e.target.value
                  })
                }
              >
                <option value="">All Gender</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={filters.current_class}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    current_class: e.target.value
                  })
                }
              >
                <option value="">All Classes</option>

                {
                  classes.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))
                }
              </select>
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() =>
                  setFilters({
                    status: '',
                    gender: '',
                    current_class: ''
                  })
                }
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>
            Students List ({count} total)
          </span>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-success"
              onClick={exportStudents}
            >
             <i className="bi bi-file-earmark-arrow-down icon-sm"></i> Sample File
            </button>
            <label className="btn btn-sm btn-outline-primary mb-0">
              <i className="bi bi-file-earmark-arrow-up icon-sm"></i> Bulk Upload
              <input
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={importStudents}
              />
            </label>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={students}
          loading={loading}
        />

        <div className="card-body pt-0">
          <Pagination
            count={count}
            page={page}
            pageSize={15}
            onChange={setPage}
          />
        </div>
      </div>

      {/* ADD / EDIT MODAL */}
      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={editStudent ? 'Edit Student' : 'Admit New Student'}
        size="xl"
      >

        <StudentForm
          student={editStudent}
          classes={classes}
          onSave={() => {
            setShowModal(false);
            fetchStudents();
          }}
          onCancel={() => setShowModal(false)}
        />

      </Modal>

      {/* VIEW MODAL */}
           <Modal
                  show={showViewModal}
                  onClose={() => setShowViewModal(false)}
                  title="Student Details"
                  size="lg"
                >
          
                  {viewStudent && (
          
                    <div className="container-fluid">
          
                      <div className="row g-3">
          
                        {/* <div className="col-md-12">
                          <div className='student-pic' style={{width:"80px", height:"80px", borderRadius:"20px",border:"solid 1px #ededed"}}>
                            <img className='img-fluid' src='https://png.pngtree.com/png-clipart/20240321/original/pngtree-avatar-job-student-flat-portrait-of-man-png-image_14639683.png'/>
                          </div>
                        </div> */}
          
                        <div className="col-md-12">
                          <div
                            className="student-pic overflow-hidden"
                            style={{
                              width: "80px",
                              height: "80px",
                              borderRadius: "20px",
                              border: "solid 1px #ededed"
                            }}
                          >
                            <img
                              className="img-fluid w-100 h-100"
                              style={{ objectFit: "cover" }}
                              src={
                                viewStudent?.photo ||
                                "https://png.pngtree.com/png-clipart/20240321/original/pngtree-avatar-job-student-flat-portrait-of-man-png-image_14639683.png"
                              }
                              alt="student"
                            />
          
                          </div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Admission No:</strong>
                          <div>{viewStudent.admission_number || '—'}</div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Student Name:</strong>
                          <div>
                            {viewStudent.full_name ||
                              `${viewStudent.first_name} ${viewStudent.last_name}`}
                          </div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Gender:</strong>
                          <div>
                            {viewStudent.gender === 'M'
                              ? 'Male'
                              : viewStudent.gender === 'F'
                                ? 'Female'
                                : 'Other'}
                          </div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Date of Birth:</strong>
                          <div>
                            {viewStudent.date_of_birth
                              ? formatDate(viewStudent.date_of_birth)
                              : '—'}
                          </div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Phone:</strong>
                          <div>{viewStudent.phone || '—'}</div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Email:</strong>
                          <div>{viewStudent.email || '—'}</div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Class:</strong>
                          <div>
                            {viewStudent.class_name || '—'}
                            {viewStudent.section_name
                              ? ` - ${viewStudent.section_name}`
                              : ''}
                          </div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Roll Number:</strong>
                          <div>{viewStudent.roll_number || '—'}</div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Status:</strong>
                          <div>
                            <StatusBadge status={viewStudent.status} />
                          </div>
                        </div>
          
                        <div className="col-12">
                          <hr className='mt-0'  />
                        </div>
          
                        <div className="col-md-4">
                          <strong>Father Name:</strong>
                          <div>{viewStudent.father_name || '—'}</div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Father Phone:</strong>
                          <div>{viewStudent.father_phone || '—'}</div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Father Occupation:</strong>
                          <div>{viewStudent.father_occupation || '—'}</div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Mother Name:</strong>
                          <div>{viewStudent.mother_name || '—'}</div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Mother Phone:</strong>
                          <div>{viewStudent.mother_phone || '—'}</div>
                        </div>
          
                        <div className="col-md-4">
                          <strong>Mother Occupation:</strong>
                          <div>{viewStudent.mother_occupation || '—'}</div>
                        </div>
          
                        <div className="col-12">
                          <hr className='mt-0' />
                        </div>
          
                       
          
                        <div className="col-md-4">
                          <strong>Country:</strong>
                          <div>{viewStudent.country || '—'}</div>
                        </div>

                        <div className="col-md-4">
                          <strong>State:</strong>
                          <div>{viewStudent.state || '—'}</div>
                        </div>

                        <div className="col-md-4">
                          <strong>City:</strong>
                          <div>{viewStudent.city || '—'}</div>
                        </div>
                         
                        <div className="col-md-4">
                          <strong>Pincode:</strong>
                          <div>{viewStudent.pincode || '—'}</div>
                        </div>

                         <div className="col-md-8">
                          <strong>Address:</strong>
                          <div>{viewStudent.address || '—'}</div>
                        </div>
          
                      </div>
          
                    </div>
          
                  )}
          
                </Modal>

      {/* <Modal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Student Details"
        size="lg"
      >

        {
          viewStudent && (

            <div className="container-fluid">

              <div className="row g-3">

                <div className="col-md-4">
                  <strong>Admission No:</strong>
                  <div>{viewStudent.admission_number}</div>
                </div>

                <div className="col-md-4">
                  <strong>Name:</strong>
                  <div>
                    {viewStudent.first_name} {viewStudent.last_name}
                  </div>
                </div>

                <div className="col-md-4">
                  <strong>Phone:</strong>
                  <div>{viewStudent.phone}</div>
                </div>

              </div>

            </div>
          )
        }

      </Modal> */}

    </div>
  );
};

export default StudentList;